import React, { useState } from 'react';
import { useTheme } from '../../context/ThemeContext';
import ActivityStatus from './ActivityStatus';
import CollapsibleCard from './CollapsibleCard';
import { FileTextIcon } from '../icons';
import type { SharePointActivity, SharePointResultsPart, SharePointSource } from './types';

interface MergedSource extends SharePointSource {
  read: boolean;
}

// One answer can search and then read several documents; showing a card per
// tool call would stack near-duplicates, so they are merged per message.
export function mergeSharePointSources(parts: SharePointResultsPart[]): MergedSource[] {
  const byUrl = new Map<string, MergedSource>();
  for (const part of parts) {
    for (const source of part.results) {
      const key = source.url || source.title;
      const existing = byUrl.get(key);
      const read = part.action === 'read' || !!existing?.read;
      byUrl.set(key, { ...existing, ...source, summary: source.summary || existing?.summary, read });
    }
  }

  return Array.from(byUrl.values()).sort((a, b) => Number(b.read) - Number(a.read));
}

const formatDate = (value?: string) => {
  if (!value) return '';
  const date = new Date(value);

  return isNaN(date.getTime()) ? '' : date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const ACTIVITY_LABELS: Record<SharePointActivity, string> = {
  searching: 'Searching SharePoint…',
  reading: 'Reading in SharePoint…',
  browsing: 'Browsing SharePoint…',
  summarizing: 'Summarising a long document, part by part…',
  comparing: 'Comparing document versions…',
};

const MAX_PREVIEW_CHIPS = 3;

export const SharePointStatus: React.FC<{ status: SharePointActivity }> = ({ status }) => (
  <ActivityStatus icon={<FileTextIcon size={14} />} label={ACTIVITY_LABELS[status] ?? ACTIVITY_LABELS.searching} />
);

const SharePointResults: React.FC<{ sources: MergedSource[] }> = ({ sources }) => {
  const { colors } = useTheme();
  const [isExpanded, setIsExpanded] = useState(false);

  if (sources.length === 0) {
    return null;
  }

  const readCount = sources.filter((s) => s.read).length;
  const hiddenCount = sources.length - MAX_PREVIEW_CHIPS;

  const preview = (
    <div className="cv-source-chips">
      {sources.slice(0, MAX_PREVIEW_CHIPS).map((source, index) => (
        source.url.startsWith('https://') ? (
          <a key={index} className="cv-source-chip" href={source.url} target="_blank" rel="noopener noreferrer" title={source.title}>
            <FileTextIcon size={14} />
            <span className="cv-source-chip__label">{source.title}</span>
          </a>
        ) : (
          <span key={index} className="cv-source-chip" title={source.title}>
            <FileTextIcon size={14} />
            <span className="cv-source-chip__label">{source.title}</span>
          </span>
        )
      ))}
      {hiddenCount > 0 && (
        <button type="button" className="cv-source-chip cv-source-chip--more" onClick={() => setIsExpanded(true)}>
          +{hiddenCount} more
        </button>
      )}
    </div>
  );

  return (
    <CollapsibleCard
      icon={<FileTextIcon size={16} />}
      title="SharePoint sources"
      expanded={isExpanded}
      onExpandedChange={setIsExpanded}
      badges={
        <span className="cv-badge">
          {readCount > 0 ? `${readCount} read · ` : ''}{sources.length} {sources.length === 1 ? 'document' : 'documents'}
        </span>
      }
      preview={preview}
    >
      {sources.map((source, index) => {
        const isSafeLink = source.url.startsWith('https://');
        const meta = [source.site, formatDate(source.last_modified)].filter(Boolean).join(' · ');

        return (
          <div key={index} className="cv-list-item">
            <span style={{ display: 'flex', color: colors.textMuted, paddingTop: '2px' }}><FileTextIcon size={16} /></span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                {isSafeLink ? (
                  <a href={source.url} target="_blank" rel="noopener noreferrer" style={{ fontWeight: 500, fontSize: '14px' }}>
                    {source.title}
                  </a>
                ) : (
                  <span style={{ fontWeight: 500, fontSize: '14px', color: colors.textPrimary }}>{source.title}</span>
                )}
                {source.kind === 'page' && <span className="cv-badge">Page</span>}
                {source.read && <span className="cv-badge cv-badge--success">Read</span>}
              </div>
              {meta && (
                <div style={{ fontSize: '12px', color: colors.textMuted, marginTop: '1px' }}>{meta}</div>
              )}
              {source.summary && (
                <div className="cv-clamp-3" style={{ fontSize: '13px', color: colors.textSecondary, lineHeight: '1.45', marginTop: '4px' }}>
                  {source.summary}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </CollapsibleCard>
  );
};

export default SharePointResults;
